import {Component, OnDestroy, OnInit} from '@angular/core';
import {AuthProviderEnum} from "../enum/auth-provider-enum";
import {User} from "@auth0/auth0-angular";
import {Auth0Service} from "../../auth0.service";
import {FormControl, Validators} from "@angular/forms";
import {RequestMapperService} from "../../request-mapper.service";
import {RaintreeResponse} from "../../raintree/interface/raintree-response";
import {TenantResponse} from "../../raintree/interface/tenant-response/tenant-response";
import {
	TenantVerificationTransmissionService
} from "../../raintree/transmission/tenant-verification-transmisssion/tenant-verification-transmission.service";
import {TenantVerification} from "../interface/tenant-verification";
import {RegistrationData} from "../interface/registration-data";
import {AuthenticationService} from "../authentication.service";
import {AuthTransmissionService} from "../../raintree/transmission/auth-transmission/auth-transmission.service";
import {ToastMessageService} from "../../services/toast-message.service";
import {BloomsightService} from "../../bloomsight.service";

@Component({
  selector: 'fb-authentication',
  templateUrl: './authentication.component.html',
  styleUrls: ['./authentication.component.scss']
})
export class AuthenticationComponent implements OnInit, OnDestroy {

	public authProvider: AuthProviderEnum = AuthProviderEnum.UNKNOWN;
	public emailFormControl: FormControl = new FormControl('', [
		Validators.required,
		Validators.email
	]);
	public shouldLogin: boolean = false;
	public shouldRegister: boolean = false;

	public tenantStatus: TenantVerification = {
		registered: false,
		emailVerified: false,
	};

	constructor(
		private _auth0: Auth0Service,
		private _tenantTransmission: TenantVerificationTransmissionService,
		private _fabricAuth: AuthenticationService,
		private _authTransmission: AuthTransmissionService,
		private _toast: ToastMessageService,
		private _bloomsight: BloomsightService
	) {
	}

	ngOnInit() {
	}

	public selectAuthProvider($event: AuthProviderEnum) {
		this.authProvider = $event;
		if (this.authProvider === AuthProviderEnum.GOOGLE) {
			this.authWithGoogle();
		}
		if (this.authProvider === AuthProviderEnum.UNKNOWN) {
			this.emailFormControl.reset();
			this.tenantStatus = {
				registered: false,
				emailVerified: false
			}
			this.shouldRegister = false;
			this.shouldLogin = false;
		}
	}

	public authWithGoogle(): void {
		this._auth0.googleLoginPopup().subscribe(
			() => {},
			(error) => {
				this._toast.error('Google pop up was closed', 'You must chose an account to proceed further');
				this.selectAuthProvider(AuthProviderEnum.UNKNOWN);
			}
		);
		this.authServiceInit();
	}
	public authWithFacebook(): void {
		this._auth0.facebookLoginPopup().subscribe(
			() => {},
			(error) => {
				this._toast.error('Facebook pop up was closed', 'You must chose an account to proceed further');
				this.selectAuthProvider(AuthProviderEnum.UNKNOWN);
			}
		);
		this.authServiceInit();
	}

	public authServiceInit(): void {
		this._auth0.onError((error) => {
			this._toast.error(
				error.name,
				error.message
			);
		});

		this._auth0.init((user: User | null | undefined) => {
			if(!user) {
				return;
			}
			if (user.email) {
				this._verifyTenant(user.email);
			}
		});
	}

	protected readonly AuthProviderEnum = AuthProviderEnum;

	public getUserEmail(value: string) {
		this._verifyTenant(value);
	}

	private _verifyTenant(tenantEmail: string): void {
		this.emailFormControl.setValue(tenantEmail.trim().toLowerCase());
		this._tenantTransmission.verifyTenant(
			RequestMapperService.VERIFY_TENANT,
			{
				email: tenantEmail
			},
			() => { },
			() => { },
			(response: RaintreeResponse | TenantResponse) => {
				this.tenantStatus = response['entity'];
				this._continueAuthFlow();
			},
			(error: string) => {
			},
			() => {
			}
		)
	}

	private _verifyProvider(): void {
		this._authTransmission.verifyProvider(
			{
				username: this.emailFormControl.value,
				provider: this.authProvider
			},
			() => {},
			() => {},
			() => {
				this._continueLoginFlow();
			},
			(error) => {
				if (error['provider'] === 0){
					// this.selectAuthProvider(AuthProviderEnum.BASIC);
					this._continueLoginFlow();
				}
				else if(error['provider']=== 2) {
					this.selectAuthProvider(AuthProviderEnum.GOOGLE);
				}
				else if(error['provider'] === 3) {
					this.selectAuthProvider(AuthProviderEnum.FACEBOOK);
				}
			},
			() => {
			}
		)
	}

	private _continueAuthFlow(): void {
		if(this.tenantStatus.registered) {
			if(this.authProvider === AuthProviderEnum.BASIC)
			{
				this._verifyProvider();
			}
			else {
				this._continueLoginFlow();
			}

		}
		else {
			this._continueRegistrationFlow();
		}
	}

	private _continueRegistrationFlow(): void {
		if (this.authProvider === AuthProviderEnum.BASIC)
			this.shouldRegister = true;
		else {
			this._auth0.getIdToken(
				(token) => {
					if (token === null || token === undefined)
						return;
					if(token.email && token.__raw) {
						const registeredUser: RegistrationData = this._mapRegisterData(
							token.email.trim().toLowerCase(),
							token.__raw,
							this.authProvider
						)

						this._registerTenant(registeredUser);
					}
				}
			)
		}
	}

	private _continueLoginFlow(): void {
		if (this.tenantStatus.emailVerified && this.authProvider === AuthProviderEnum.BASIC) {
			this.shouldLogin = true;
		}
		if (this.authProvider === AuthProviderEnum.GOOGLE) {
			this._auth0.getIdToken(
				(token) => {
					if (token === null || token === undefined)
						return;

					this.oAuthLoginRequest(
						this.emailFormControl.value,
						token.__raw
					)
				}
			)
		}
	}

	public oAuthLoginRequest(username: string, password: string): void {
		this._fabricAuth.authenticateSocial(
			username,
			password,
			this.authProvider,
			() => {
				this._toast.success('Logged In Successfully', 'User logged in successfully');
			},
			() => {
				this.selectAuthProvider(AuthProviderEnum.UNKNOWN);
			},
			() => {

			}
		)
	}

	private _mapRegisterData(
		email: string,
		password: string,
		provider: AuthProviderEnum
	): RegistrationData {
		return {
			tenant: {
				email: email,
				password: password,
				provider: provider
			}
		}
	}

	private _registerTenant(
		data: RegistrationData
	): void {
		this._authTransmission.register(
			RequestMapperService.REGISTER_SOCIAL,
			data,
			() => {},
			() => {},
			(response: RaintreeResponse) => {
				this.oAuthLoginRequest(
					data.tenant.email,
					data.tenant.password
				);
				this._bloomsight.trackDataEvent(
					'671a29d522fb296d61f458a4',
					{email: data.tenant.email.trim().toLowerCase()},
					'New Registration'
				);
			},
			(error: string) => {
				this.selectAuthProvider(AuthProviderEnum.UNKNOWN);
			},
			() => {
			}
		)
	}


	ngOnDestroy() {
		this._auth0.destroyAll();
	}
}
